import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { usePermissions } from "@/hooks/usePermissions";
import { toUserFriendlyError } from "@/lib/error-messages";
import ConfirmLossDialog from "@/components/Crm/ConfirmLossDialog";

/** Campos editáveis inline de uma oportunidade. */
export type DealField =
  | "title"
  | "value"
  | "expectedCloseDate"
  | "description"
  | "notes"
  | "sourceId"
  | "contactId"
  | "ownerUserId"
  | "stageId";

/** Usuário fechou o diálogo de perda — reverter sem alarde. */
class CancelledError extends Error {
  constructor() {
    super("Alteração cancelada");
    this.name = "CancelledError";
  }
}

export type StageLite = {
  id: number;
  name: string;
  color?: string | null;
  isWon?: boolean | null;
  isLost?: boolean | null;
  probability?: number | null;
};

/**
 * Cada campo mora numa mutation diferente, com permissão própria:
 *
 *  · escalares   → crm.deals.update      (crm:deals_edit)
 *  · responsável → crm.deals.assign      (crm:deals_assign, + escopo de gestor)
 *  · estágio     → crm.deals.changeStage (crm:deals_change_stage)
 *
 * O servidor deixou de aceitar estágio e responsável no `update` justamente
 * porque isso contornava as outras duas permissões. Sem este hook, cada célula
 * editável teria que redescobrir esse roteamento.
 */
const FIELD_PERMISSION: Record<DealField, string> = {
  title: "crm:deals_edit",
  value: "crm:deals_edit",
  expectedCloseDate: "crm:deals_edit",
  description: "crm:deals_edit",
  notes: "crm:deals_edit",
  sourceId: "crm:deals_edit",
  contactId: "crm:deals_edit",
  ownerUserId: "crm:deals_assign",
  stageId: "crm:deals_change_stage",
};

export function useDealFieldEdit({
  stages = [],
  onOptimistic,
  onRollback,
  onSettled,
}: {
  /** Necessário para detectar estágio de perda antes de mutar. */
  stages?: StageLite[];
  /**
   * Aplica o valor na tela antes da resposta do servidor.
   *
   * `patch` são campos reais do deal; `display` é o que a tela precisa mostrar
   * mas o servidor não recebe (o nome do vendedor escolhido, por exemplo).
   * Vêm separados para que o consumidor não precise misturar chave sintética
   * dentro do objeto de domínio.
   */
  onOptimistic?: (
    dealId: number,
    patch: Record<string, unknown>,
    display?: Record<string, unknown>,
  ) => void;
  onRollback?: (dealId: number) => void;
  onSettled?: (dealId: number) => void;
} = {}) {
  const { can } = usePermissions();
  const utils = trpc.useUtils();

  const [lossPrompt, setLossPrompt] = useState<{
    dealId: number;
    toStageId: number;
    dealTitle: string;
  } | null>(null);
  // Promessa da célula que disparou a mudança para um estágio de perda. Fica
  // pendurada até o diálogo resolver, para que a célula continue em "salvando"
  // e o Esc/cancelar reverta o otimista.
  const lossResolver = useRef<{
    resolve: () => void;
    reject: (e: unknown) => void;
  } | null>(null);

  const canEditField = useCallback(
    (field: DealField) => can(FIELD_PERMISSION[field] as never),
    [can],
  );

  /**
   * Devolve a promessa da invalidação — quem chama PRECISA esperar.
   *
   * Antes era fire-and-forget (`void utils...invalidate()`) e o overlay
   * otimista da tabela era descartado no mesmo tick: a célula voltava ao valor
   * antigo e só "saltava" para o novo quando o refetch chegava, uns 200-400ms
   * depois. A tela de detalhe não sofria porque escreve direto no cache de
   * `deals.get`; a tabela depende só do overlay.
   */
  function invalidate(dealId: number, field: DealField) {
    // changeStage tem efeito colateral em comissões, relatórios e ranking
    // (ensureCommissionForWonDeal). Invalidar o CRM inteiro é grosseiro, mas
    // é a única forma de não deixar número velho em tela.
    if (field === "stageId") {
      return utils.crm.invalidate();
    }
    return Promise.all([
      utils.crm.deals.get.invalidate({ id: dealId }),
      utils.crm.deals.list.invalidate(),
      utils.crm.deals.kanban.invalidate(),
      utils.crm.deals.listActivities.invalidate({ dealId }),
    ]);
  }

  const updateMutation = trpc.crm.deals.update.useMutation();
  const assignMutation = trpc.crm.deals.assign.useMutation();
  const changeStageMutation = trpc.crm.deals.changeStage.useMutation();

  /** Patch otimista no cache do detalhe (input estável: { id }). */
  function patchDetailCache(dealId: number, patch: Record<string, unknown>) {
    const previous = utils.crm.deals.get.getData({ id: dealId });
    utils.crm.deals.get.setData({ id: dealId }, (old: any) =>
      old ? { ...old, deal: { ...old.deal, ...patch } } : old,
    );
    return previous;
  }

  const commit = useCallback(
    async (
      dealId: number,
      field: DealField,
      next: unknown,
      ctx?: { dealTitle?: string; optimistic?: Record<string, unknown> },
    ): Promise<void> => {
      if (!canEditField(field)) {
        throw new Error("Você não tem permissão para alterar este campo.");
      }

      const patch = { [field]: next } as Record<string, unknown>;
      const previousDetail = patchDetailCache(dealId, patch);
      // `optimistic` carrega o que a tela precisa mostrar na hora mas o
      // servidor não recebe — o nome do vendedor escolhido, por exemplo: a
      // mutation manda só o id, e sem o nome a célula exibiria o do
      // responsável anterior até a query voltar. Vai separado do patch para
      // não entrar no objeto do deal.
      onOptimistic?.(dealId, patch, ctx?.optimistic);

      const rollback = () => {
        if (previousDetail !== undefined) {
          utils.crm.deals.get.setData({ id: dealId }, previousDetail as never);
        }
        onRollback?.(dealId);
      };

      try {
        if (field === "stageId") {
          const target = stages.find((s) => s.id === next);
          if (target?.isLost) {
            // Perda exige motivo — mesma regra do stepper da tela de detalhe.
            await new Promise<void>((resolve, reject) => {
              lossResolver.current = { resolve, reject };
              setLossPrompt({
                dealId,
                toStageId: next as number,
                dealTitle: ctx?.dealTitle ?? "",
              });
            });
          } else {
            await changeStageMutation.mutateAsync({
              id: dealId,
              toStageId: next as number,
            });
          }
        } else if (field === "ownerUserId") {
          await assignMutation.mutateAsync({
            id: dealId,
            ownerUserId: (next as number | null) ?? null,
          });
        } else {
          await updateMutation.mutateAsync({ id: dealId, ...patch } as never);
        }
      } catch (err) {
        rollback();
        // Fechar o diálogo de perda é uma decisão do usuário, não uma falha —
        // reverte em silêncio, sem toast de erro.
        if (err instanceof CancelledError) throw err;
        const msg = toUserFriendlyError(err as never);
        toast.error(msg);
        throw new Error(msg);
      } finally {
        // O overlay só pode ser descartado DEPOIS que os dados novos chegaram,
        // senão a célula pisca de volta ao valor antigo no meio do caminho.
        await invalidate(dealId, field);
        onSettled?.(dealId);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canEditField, stages, onOptimistic, onRollback, onSettled],
  );

  async function confirmLoss(lossReasonId: number | null) {
    if (!lossPrompt) return;
    const { dealId, toStageId } = lossPrompt;
    setLossPrompt(null);
    try {
      await changeStageMutation.mutateAsync({
        id: dealId,
        toStageId,
        lossReasonId,
      });
      lossResolver.current?.resolve();
    } catch (err) {
      lossResolver.current?.reject(err);
    } finally {
      lossResolver.current = null;
    }
  }

  function cancelLoss() {
    setLossPrompt(null);
    // Rejeitar faz a célula reverter o otimista e sair de "salvando".
    lossResolver.current?.reject(new CancelledError());
    lossResolver.current = null;
  }

  const lossDialog = (
    <ConfirmLossDialog
      open={!!lossPrompt}
      onClose={cancelLoss}
      onConfirm={(lossReasonId) => void confirmLoss(lossReasonId)}
      dealTitle={lossPrompt?.dealTitle ?? ""}
    />
  );

  return { canEditField, commit, lossDialog };
}
